import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import ja from 'date-fns/locale/ja';
import { DateRangePicker, RangeKeyDict, CalendarProps } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import dayjs from 'dayjs';

import { typographyfontTextHeader } from 'src/components/calendar/styles';

type DropDownProps = {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
};

const selectionRangeDefault = {
  startDate: new Date(),
  endDate: new Date(),
  key: 'selection',
};

const DatePickerRangeCustom: React.FC<DropDownProps> = (props) => {
  const [selectionRange, setSelectionRange] = useState(selectionRangeDefault);

  const handleChange = (ranges: RangeKeyDict) => {
    const startDate = ranges.selection.startDate ? ranges.selection.startDate : null;
    const endDate = ranges.selection.endDate ? ranges.selection.endDate : null;
    props.onChange(startDate, endDate);
  };

  const convertDateToDayjs = (date: Date | null) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return dayjs(`${year}/${month}/${day}`);
  };

  const addMonth = (date: Date, number: number) => {
    const day = convertDateToDayjs(date);
    const dayFormat = day ? day.add(number, 'month').format('YYYY/MM/DD') : '';
    return new Date(dayFormat);
  }

  const subMonth = (date: Date, number: number) => {
    const day = convertDateToDayjs(date);
    const dayFormat = day ? day.subtract(number, 'month').format('YYYY/MM/DD') : '';
    return new Date(dayFormat);
  }

  useEffect(() => {
    setSelectionRange((prevState) => ({
      ...prevState,
      startDate: props.startDate ? props.startDate : selectionRangeDefault.startDate,
      endDate: props.endDate ? props.endDate : selectionRangeDefault.endDate,
    }));
  }, [props.startDate, props.endDate]);

  const renderMonthAndYear = (currentFocusedDate: Date, changeShownDate: (value: Date | number | string, mode?: "set" | "setYear" | "setMonth" | "monthOffset") => void, props: CalendarProps) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '5px 16px 0px' }}>
        <IconButton onClick={() => changeShownDate(subMonth(currentFocusedDate, 1))} title='Previous month'>
          <img src='/images/ic-arrow-left-gray.svg' style={{ width: '24px', height: '24px' }} alt='icon' />
        </IconButton>
        <Typography variant='body2' sx={{ ...typographyfontTextHeader, color: '#434154', mx: '5px' }}>
          {currentFocusedDate.getFullYear()}年{currentFocusedDate.getMonth() + 1}月 ~ {addMonth(currentFocusedDate, 1).getFullYear()}年{addMonth(currentFocusedDate, 1).getMonth() + 1}月
        </Typography>
        <IconButton onClick={() => changeShownDate(addMonth(currentFocusedDate, 1))} title='Next month'>
          <img src='/images/ic-arrow-right-gray.svg' style={{ width: '24px', height: '24px' }} alt='icon' />
        </IconButton>
      </Box>
    );
  };

  return (
    <Box sx={{
      '.rdrDefinedRangesWrapper': { display: 'none' },
      '.rdrDateRangePickerWrapper': {
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
        borderRadius: '4px',
        width: '500px',
        '.rdrDateRangeWrapper': {
          width: '500px',
        },
      }
    }}>
      <DateRangePicker
        ranges={[selectionRange]}
        direction='horizontal'
        moveRangeOnFirstSelection={false}
        months={2}
        locale={ja}
        showDateDisplay={false}
        showMonthAndYearPickers={false}
        staticRanges={[]}
        inputRanges={[]}
        onChange={handleChange}
        navigatorRenderer={renderMonthAndYear}
      />
    </Box>
  );
};

export default DatePickerRangeCustom;
